/**
 * Client for the /api/form-token endpoint + the honeypot value collected
 * from auth-form hidden inputs. Used by lib/auth.ts to attach X-Form-Token
 * + X-Honeypot headers to SuperTokens auth calls — defense-in-depth bot
 * floor alongside Cloudflare Turnstile.
 *
 * Singleton because the auth-form -> SuperTokens flow has no good handoff
 * point for passing extra headers, and the form-token TTL is 30min so one
 * fetch covers the whole session of multiple form interactions.
 */

const API_DOMAIN = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');
const TOKEN_TTL_MS = 25 * 60_000; // refresh before the 30min server-side window

let cachedToken: string = '';
let cachedAt: number = 0;
let inflight: Promise<string> | null = null;

let honeypotValue: string = '';

async function fetchToken(): Promise<string> {
  try {
    const res = await fetch(`${API_DOMAIN}/api/form-token`, { credentials: 'include' });
    if (!res.ok) return '';
    const body = await res.json() as { token?: string };
    return body.token || '';
  } catch {
    return '';
  }
}

/**
 * Get a fresh form token. Lazily fetched on first call, cached for ~25min,
 * coalesces concurrent callers into a single fetch.
 */
export async function getFormToken(): Promise<string> {
  const now = Date.now();
  if (cachedToken && now - cachedAt < TOKEN_TTL_MS) {
    return cachedToken;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    const t = await fetchToken();
    cachedToken = t;
    cachedAt = Date.now();
    inflight = null;
    return t;
  })();
  return inflight;
}

export function setHoneypotValue(v: string): void {
  honeypotValue = v;
}

export function getHoneypotValue(): string {
  return honeypotValue;
}

export function resetHoneypotValue(): void {
  honeypotValue = '';
}

/**
 * Build the X-Form-Token + X-Honeypot header object used by lib/auth.ts.
 * Headers are always returned (empty string is harmless when middleware
 * is fail-open).
 */
export async function getBotFallbackHeaders(): Promise<Record<string, string>> {
  const token = await getFormToken();
  return {
    'X-Form-Token': token,
    'X-Honeypot': honeypotValue,
  };
}
